import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import { ReservationReturn } from '../clients/rust-api-client';
import { formatMoney } from '../utils/billingHelpers';
import {
  formatLocalRange,
  isZeroMoney,
  statusLabel,
} from '../reservation/reservationMeta';
import { inputClass, labelClass } from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * The bookings a closure or an archive would land on (the API's 409).
 *
 * Nothing is cancelled until the manager says so here, with a reason the
 * households are sent. Confirming cancels every listed booking and
 * reverses its fee — a closure is the association's doing, so nobody
 * keeps paying for a slot that was taken away.
 */
interface Props {
  open: boolean;
  /** What is being done: "close the clubhouse", "archive the pool". */
  action: string;
  conflicts: ReservationReturn[];
  onClose: () => void;
  /** Retry with cancel-and-reverse; resolve or throw with the API error. */
  onConfirm: (reason: string) => Promise<void>;
  error?: string | null;
}

export const ConflictDialog: React.FC<Props> = ({
  open,
  action,
  conflicts,
  onClose,
  onConfirm,
  error,
}) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSaving(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setSaving(false);
    }
  };

  const thisYear = new Date().getFullYear();
  const paid = conflicts.filter((c) => !isZeroMoney(c.fee_amount) && c.fee_status === 'posted');

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <form onSubmit={submit}>
        <DialogTitle>
          {conflicts.length === 1
            ? '1 booking is in the way'
            : `${conflicts.length} bookings are in the way`}
        </DialogTitle>
        <DialogDescription>
          To {action}, these must be cancelled. Each household is notified with your reason
          {paid.length > 0 ? ', and every fee is reversed' : ''}.
        </DialogDescription>
        <DialogBody className="space-y-4">
          <ul className="max-h-64 divide-y divide-line/60 overflow-y-auto rounded-lg border border-line text-[13px]">
            {conflicts.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                <span className="min-w-0 flex-1">
                  <Link
                    to={`/hoa/${c.hoa_id}/reservations/${c.id}`}
                    className="font-medium text-fg hover:underline"
                    target="_blank"
                  >
                    {c.home_address}
                  </Link>
                  <span className="block text-[12px] text-fg-muted">
                    {formatLocalRange(c.start_local, c.end_local, thisYear)} · {statusLabel(c.status)}
                  </span>
                </span>
                {!isZeroMoney(c.fee_amount) && (
                  <span className="text-[12px] text-fg-muted">{formatMoney(c.fee_amount)}</span>
                )}
              </li>
            ))}
          </ul>
          <label className={labelClass}>
            Reason (required — sent to each household)
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. The clubhouse floor is being refinished."
              className={clsx(inputClass, 'resize-none')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Back
          </Button>
          <Button color="red" type="submit" disabled={saving || !reason.trim()}>
            {saving ? 'Cancelling…' : `Cancel ${conflicts.length === 1 ? 'it' : 'them'} and continue`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ConflictDialog;
